import { isBlockedTrackingHost } from './fbpixel';

/* eslint-disable @typescript-eslint/no-explicit-any */

type OpenAIQueue = ((...args: any[]) => void) & {
  q?: any[][];
};

type MetaFunction = ((...args: any[]) => unknown) & {
  [key: string]: any;
  callMethod?: (...args: any[]) => unknown;
  queue?: IArguments[];
  push?: (...args: any[]) => unknown;
  loaded?: boolean;
  version?: string;
  __openaiAdsWrapped?: boolean;
  __openaiAdsOriginal?: MetaFunction;
};

declare global {
  interface Window {
    oaiq?: OpenAIQueue;
    __openaiAdsBridgeInstalled?: boolean;
  }
}

const PIXEL_ID =
  (import.meta.env.VITE_OPENAI_ADS_PIXEL_ID as string | undefined) ??
  'Aj7kidcTi657Z4Btemr1xK';
const CAPI_URL =
  (import.meta.env.VITE_OPENAI_ADS_CAPI_URL as string | undefined) ||
  'https://mediloop.ai/api/openai-conversion';

const OPENAI_OPPREF_COOKIE = 'mediloop_oppref';
const OPENAI_OBREF_COOKIE = 'mediloop_obref';
const HASH_RE = /^[a-f0-9]{64}$/i;

const META_TO_OPENAI_EVENT: Record<
  string,
  { name: string; dataType: 'contents' | 'customer_action' }
> = {
  Lead: { name: 'lead_created', dataType: 'customer_action' },
  Purchase: { name: 'order_created', dataType: 'contents' },
  InitiateCheckout: { name: 'checkout_started', dataType: 'contents' },
  CompleteRegistration: {
    name: 'registration_completed',
    dataType: 'customer_action',
  },
  AddToCart: { name: 'items_added', dataType: 'contents' },
  ViewContent: { name: 'contents_viewed', dataType: 'contents' },
};

let cachedEmailSha256: string | undefined;
let cachedExternalId: string | undefined;
let cachedExternalIdSha256: string | undefined;
let pendingUserUpdate: Promise<void> = Promise.resolve();

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${escapeRegex(name)}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeSharedCookie(name: string, value: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const host = window.location.hostname.toLowerCase();
  const domain =
    host === 'mediloop.ai' || host.endsWith('.mediloop.ai')
      ? '; Domain=.mediloop.ai'
      : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = `${name}=${encodeURIComponent(
    value.slice(0, 2048),
  )}; Path=/; Max-Age=${60 * 60 * 24 * 90}; SameSite=Lax${domain}${secure}`;
}

function getQueryParam(name: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return new URLSearchParams(window.location.search).get(name) || undefined;
  } catch {
    return undefined;
  }
}

function persistOpenAIReferences(): void {
  const oppref = getQueryParam('oppref');
  if (oppref) writeSharedCookie(OPENAI_OPPREF_COOKIE, oppref);
}

function syncOpenAIBrowserReference(): () => void {
  let intervalId: number | undefined;
  let timeoutId: number | undefined;

  const sync = (): boolean => {
    const obref = readCookie('__obref');
    if (!obref) return false;
    writeSharedCookie(OPENAI_OBREF_COOKIE, obref);
    return true;
  };

  if (!sync()) {
    intervalId = window.setInterval(() => {
      if (sync() && intervalId) window.clearInterval(intervalId);
    }, 250);
    timeoutId = window.setTimeout(() => {
      if (intervalId) window.clearInterval(intervalId);
    }, 30_000);
  }

  return () => {
    if (intervalId) window.clearInterval(intervalId);
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}

function ensureOpenAIQueue(): OpenAIQueue | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!window.oaiq) {
    const queue: OpenAIQueue = function (...args: any[]) {
      (queue.q = queue.q || []).push(args);
    };
    queue.q = [];
    window.oaiq = queue;
  }
  return window.oaiq;
}

function loadOpenAIPixel(): void {
  const oaiq = ensureOpenAIQueue();
  if (!oaiq) return;

  if (!document.getElementById('openai-ads-pixel-sdk')) {
    const script = document.createElement('script');
    script.id = 'openai-ads-pixel-sdk';
    script.async = true;
    script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    document.head.appendChild(script);
  }

  oaiq('init', { pixelId: PIXEL_ID });
}

async function sha256Hex(
  value: string,
  { lowercase = false }: { lowercase?: boolean } = {},
): Promise<string | undefined> {
  try {
    const normalized = lowercase ? value.trim().toLowerCase() : value.trim();
    if (!normalized) return undefined;
    const bytes = new TextEncoder().encode(normalized);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
}

function firstValue(value: unknown): string | undefined {
  const candidate = Array.isArray(value) ? value[0] : value;
  return typeof candidate === 'string' && candidate.trim()
    ? candidate.trim()
    : undefined;
}

async function normalizeEmailHash(value: unknown): Promise<string | undefined> {
  const candidate = firstValue(value);
  if (!candidate) return undefined;
  return HASH_RE.test(candidate)
    ? candidate.toLowerCase()
    : sha256Hex(candidate, { lowercase: true });
}

async function normalizeExternalId(
  value: unknown,
): Promise<{ raw?: string; hash?: string }> {
  const candidate = firstValue(value);
  if (!candidate) return {};
  if (HASH_RE.test(candidate)) return { hash: candidate.toLowerCase() };
  return { raw: candidate, hash: await sha256Hex(candidate) };
}

async function updateOpenAIUser(metaUser: unknown): Promise<void> {
  if (!metaUser || typeof metaUser !== 'object' || Array.isArray(metaUser)) return;
  const userData = metaUser as Record<string, unknown>;

  const [emailSha256, externalId] = await Promise.all([
    normalizeEmailHash(userData.em),
    normalizeExternalId(userData.external_id),
  ]);

  if (emailSha256) cachedEmailSha256 = emailSha256;
  if (externalId.raw) cachedExternalId = externalId.raw;
  if (externalId.hash) cachedExternalIdSha256 = externalId.hash;

  const user = {
    ...(cachedEmailSha256 ? { email_sha256: cachedEmailSha256 } : {}),
    ...(cachedExternalIdSha256
      ? { external_id_sha256: cachedExternalIdSha256 }
      : {}),
  };

  if (Object.keys(user).length) {
    ensureOpenAIQueue()?.('init', { user });
  }
}

function queueUserUpdate(metaUser: unknown): Promise<void> {
  pendingUserUpdate = pendingUserUpdate
    .then(() => updateOpenAIUser(metaUser))
    .catch(() => undefined);
  return pendingUserUpdate;
}

function normalizeCurrency(value: unknown): string {
  const currency = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
}

function toMinorUnits(value: unknown): number | undefined {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return undefined;
  return Math.round(amount * 100);
}

function buildContents(
  properties: Record<string, unknown>,
): Array<Record<string, unknown>> | undefined {
  const rawIds = properties.content_ids;
  const ids = rawIds == null ? [] : Array.isArray(rawIds) ? rawIds : [rawIds];
  const name =
    typeof properties.content_name === 'string'
      ? properties.content_name.slice(0, 256)
      : undefined;
  const quantity = Number(properties.num_items ?? properties.quantity ?? 1);

  if (!ids.length && !name) return undefined;

  const normalizedIds = ids.length ? ids : [name];
  return normalizedIds.slice(0, 20).map((id, index) => ({
    id: String(id).slice(0, 256),
    ...(name ? { name } : {}),
    content_type:
      typeof properties.content_type === 'string' && properties.content_type
        ? properties.content_type.slice(0, 128)
        : 'product',
    quantity:
      index === 0 && Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
  }));
}

function buildOpenAIEvent(
  metaEvent: unknown,
  metaProperties: unknown,
): { eventName: string; data: Record<string, unknown> } | null {
  if (typeof metaEvent !== 'string') return null;
  const mapping = META_TO_OPENAI_EVENT[metaEvent];
  if (!mapping) return null;

  const properties =
    metaProperties && typeof metaProperties === 'object' && !Array.isArray(metaProperties)
      ? (metaProperties as Record<string, unknown>)
      : {};
  const data: Record<string, unknown> = { type: mapping.dataType };

  if (mapping.dataType === 'contents') {
    const amount = toMinorUnits(properties.value);
    if (amount != null) {
      data.amount = amount;
      data.currency = normalizeCurrency(properties.currency);
    }
    const contents = buildContents(properties);
    if (contents?.length) data.contents = contents;
  }

  return { eventName: mapping.name, data };
}

function generateEventId(eventName: string): string {
  if (typeof crypto.randomUUID === 'function') {
    return `${eventName}_${crypto.randomUUID()}`;
  }
  return `${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function currentOppref(): string | undefined {
  return (
    getQueryParam('oppref') ||
    readCookie(OPENAI_OPPREF_COOKIE) ||
    readCookie('__oppref')
  );
}

function currentObref(): string | undefined {
  return readCookie('__obref') || readCookie(OPENAI_OBREF_COOKIE);
}

function sendServerEvent(params: {
  eventId: string;
  eventType: string;
  eventData: Record<string, unknown>;
}): void {
  void fetch(CAPI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_id: params.eventId,
      event_type: params.eventType,
      event_data: params.eventData,
      source_url: window.location.href,
      oppref: currentOppref(),
      obref: currentObref(),
      email_sha256: cachedEmailSha256,
      external_id: cachedExternalId || readCookie('mediloop_vid'),
      external_id_sha256: cachedExternalIdSha256,
    }),
    keepalive: true,
  }).catch(() => {
    // OpenAI CAPI is best-effort and must never interrupt the customer flow.
  });
}

async function mirrorMetaCall(args: unknown[]): Promise<void> {
  const [command, metaEvent, metaProperties = {}, metaOptions = {}] = args;

  if (command === 'init') {
    await queueUserUpdate(metaProperties);
    return;
  }

  if (command !== 'track') return;
  const openAIEvent = buildOpenAIEvent(metaEvent, metaProperties);
  if (!openAIEvent) return;

  const options =
    metaOptions && typeof metaOptions === 'object' && !Array.isArray(metaOptions)
      ? (metaOptions as Record<string, any>)
      : {};

  if (options.user_data) {
    await queueUserUpdate(options.user_data);
  } else {
    await pendingUserUpdate;
  }

  const eventId =
    typeof options.eventID === 'string' && options.eventID
      ? options.eventID
      : generateEventId(openAIEvent.eventName);

  ensureOpenAIQueue()?.('measure', openAIEvent.eventName, openAIEvent.data, {
    event_id: eventId,
  });
  sendServerEvent({
    eventId,
    eventType: openAIEvent.eventName,
    eventData: openAIEvent.data,
  });
}

function proxyMetaProperty(
  wrapper: MetaFunction,
  original: MetaFunction,
  property: string,
): void {
  Object.defineProperty(wrapper, property, {
    configurable: true,
    enumerable: true,
    get() {
      return original[property];
    },
    set(value: unknown) {
      original[property] = value;
    },
  });
}

function installMetaConversionBridge(): () => void {
  let intervalId: number | undefined;
  let timeoutId: number | undefined;

  const tryInstall = (): boolean => {
    if (window.__openaiAdsBridgeInstalled) return true;
    const originalFbq = window.fbq as MetaFunction | undefined;
    if (typeof originalFbq !== 'function') return false;

    if (originalFbq.__openaiAdsWrapped) {
      window.__openaiAdsBridgeInstalled = true;
      return true;
    }

    const wrappedFbq = function (this: unknown, ...args: any[]): unknown {
      const result = originalFbq.apply(this, args);
      void mirrorMetaCall(args).catch(() => undefined);
      return result;
    } as MetaFunction;

    for (const property of ['callMethod', 'queue', 'push', 'loaded', 'version']) {
      proxyMetaProperty(wrappedFbq, originalFbq, property);
    }
    wrappedFbq.__openaiAdsWrapped = true;
    wrappedFbq.__openaiAdsOriginal = originalFbq;

    window.fbq = wrappedFbq as (...args: any[]) => void;
    if (window._fbq === originalFbq) {
      window._fbq = wrappedFbq as (...args: any[]) => void;
    }
    window.__openaiAdsBridgeInstalled = true;
    return true;
  };

  if (!tryInstall()) {
    intervalId = window.setInterval(() => {
      if (tryInstall() && intervalId) window.clearInterval(intervalId);
    }, 50);
    timeoutId = window.setTimeout(() => {
      if (intervalId) window.clearInterval(intervalId);
    }, 30_000);
  }

  return () => {
    if (intervalId) window.clearInterval(intervalId);
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}

/**
 * Initialize OpenAI Ads measurement in the customer portal and bridge the
 * existing Meta-standard conversion calls to OpenAI standard events. The same
 * event ID is reused for the browser Pixel and server Conversions API legs.
 */
export function initOpenAIAds(): void {
  if (!PIXEL_ID || isBlockedTrackingHost()) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  persistOpenAIReferences();
  loadOpenAIPixel();
  void queueUserUpdate({ external_id: readCookie('mediloop_vid') });

  const stopBridgePolling = installMetaConversionBridge();
  const stopObrefSync = syncOpenAIBrowserReference();

  window.addEventListener(
    'pagehide',
    () => {
      stopBridgePolling();
      stopObrefSync();
    },
    { once: true },
  );
}
